import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConnectionStatus } from '@capacitor/network';
import { ICalendarDay, IOrder, IReservation, IReservationWhere } from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import { Observable } from 'rxjs';
import { BASE_API_URL } from '../network/api-config';
import { NetworkStatusService } from '../network/network-status.service';

const API_URL = BASE_API_URL + 'reservation';

/**
 * Local fork of @curbnturf/reservation's ReservationService — trimmed to
 * plain HttpClient (no cachableResponse behavior — that only ever mattered
 * under the real CachedHttpClient, never actually exercised here) and
 * NetworkStatusService in place of StatusFacade.networkStatus$ (same
 * underlying window online/offline signal LocalStatusFacade itself
 * delegates to).
 *
 * fetchForGuest's offline branch (native SQLite ReservationDbSet lookup) is
 * dropped rather than reimplemented — no native dbset has ever been
 * populated for cnt-landing, so that branch was unreachable in practice; this
 * matches the old LocalCachedHttpClient override's documented "offline fallback dropped,
 * not reimplemented" precedent. preload() (native-cache warmup) isn't used
 * either, so it's not ported.
 */
@Injectable({ providedIn: 'root' })
export class ReservationApiService {
  connectionStatus: ConnectionStatus = { connected: false, connectionType: 'none' };

  private readonly http = inject(HttpClient);
  private readonly networkStatus = inject(NetworkStatusService);

  constructor() {
    this.networkStatus.status$.subscribe((status) => {
      if (status) {
        this.connectionStatus = status;
      }
    });
  }

  fetch(reservationId: number): Observable<IReservation> {
    return this.http.get<IReservation>(`${API_URL}/${reservationId}`);
  }

  /** A site's real bookable-days calendar — blackout dates and other guests'
   *  confirmed reservations already subtracted server-side. Public endpoint,
   *  safe for a guest browsing a listing they don't own. */
  calendarDates(siteId: number): Observable<ICalendarDay[]> {
    return this.http.get<ICalendarDay[]>(`${API_URL}/calendar/${siteId}`);
  }

  /** Same as `calendarDates`, but the given reservation's own nights count as
   *  free — for picking new dates on an existing booking. */
  calendarDatesExcluding(siteId: number, reservationId: number): Observable<ICalendarDay[]> {
    return this.http.get<ICalendarDay[]>(`${API_URL}/calendar/${siteId}/${reservationId}`);
  }

  /** Server-priced quote for a reservation stub (`{ site, date, extras }`) —
   *  the same pricing `change`/`create` apply. Returns the stub with
   *  `priceComponents` filled in; nothing is saved. */
  quote(reservation: IReservation): Observable<IReservation> {
    return this.http.post<IReservation>(`${API_URL}/price`, reservation);
  }

  fetchForGuest(userId: number): Observable<IReservation[]> {
    return this.http.get<IReservation[]>(`${API_URL}?userId=${userId}`);
  }

  fetchForHost(userId: number): Observable<IReservation[]> {
    return this.http.get<IReservation[]>(`${API_URL}?ownerId=${userId}`);
  }

  fetchAll(query?: IReservationWhere): Observable<IReservation[]> {
    if (query) {
      const queryString = objectToUrlParams(query, true);
      return this.http.get<IReservation[]>(`${API_URL}?${queryString}`);
    } else {
      return this.http.get<IReservation[]>(`${API_URL}`);
    }
  }

  /** `requestMessage` is the guest's optional note to the host — not a
   *  reservation field; the server posts it to the booking's inbox thread. */
  create(reservation: IReservation & { requestMessage?: string }): Observable<IReservation> {
    return this.http.post<IReservation>(API_URL, reservation);
  }

  accept(reservationId: number, reason: string): Observable<IReservation> {
    return this.http.post<IReservation>(`${API_URL}/${reservationId}/accept`, { reason });
  }

  reject(reservationId: number, reason: string): Observable<IReservation> {
    return this.http.post<IReservation>(`${API_URL}/${reservationId}/reject`, { reason });
  }

  cancel(reservationId: number, reason: string): Observable<IReservation> {
    return this.http.post<IReservation>(`${API_URL}/${reservationId}/cancel`, { reason });
  }

  change(reservationId: number, order: IOrder): Observable<IReservation> {
    return this.http.put<IReservation>(`${API_URL}/${reservationId}/change`, { reservationId, order });
  }
}
